import { shell } from 'punica';

export class KeyboardShortcutsManager
  implements shell.KeyboardShortcuts.IShortcutManager
{
  #shortcuts: shell.KeyboardShortcuts.IShortcut[] = [];

  /**
   *
   * @param keys1
   * @param keys2
   * @returns
   */
  #areKeysEqual(
    keys1: shell.KeyboardShortcuts.IKey[],
    keys2: shell.KeyboardShortcuts.IKey[]
  ): boolean {
    if (keys1.length !== keys2.length) return false;

    return keys1.every(
      (key1, index) =>
        key1.key.toLowerCase() === keys2[index].key.toLowerCase() &&
        !!key1.ctrlKey === !!keys2[index].ctrlKey &&
        !!key1.shiftKey === !!keys2[index].shiftKey &&
        !!key1.altKey === !!keys2[index].altKey &&
        !!key1.metaKey === !!keys2[index].metaKey
    );
  }

  /**
   *
   * @param event
   * @param keys
   * @returns
   */
  #areKeysPressed(
    event: KeyboardEvent,
    keys: shell.KeyboardShortcuts.IKey[]
  ): boolean {
    // Strict modifier matching: undeclared modifiers must NOT be pressed,
    // otherwise "ctrl+f" also fires on "ctrl+shift+f" and which command runs
    // depends on registration order.
    return keys.every(
      (key) =>
        !!key.ctrlKey === event.ctrlKey &&
        !!key.shiftKey === event.shiftKey &&
        !!key.altKey === event.altKey &&
        !!key.metaKey === event.metaKey &&
        event.key.toLowerCase() === key.key.toLowerCase()
    );
  }

  /**
   *
   */
  constructor() {
    document.addEventListener('keydown', this.handleKeyEvent.bind(this));
  }

  /**
   * Resolve a single built-in context key to a boolean, or `undefined` when
   * the key is not one we know how to evaluate. Unknown keys are the reason
   * `#matchesContext` fails *closed*: a `when` clause referencing something we
   * cannot evaluate must NOT silently match (that is how plain `Enter` /
   * `Delete` / `Cmd+F` used to fire editor-wide).
   */
  #contextValue(key: string): boolean | undefined {
    switch (key) {
      case 'editorTextFocus':
        return this.#isTextInputFocused();
      case 'fileExplorerFocus':
        return this.#isViewFocused('primarySidebar', 'file-explorer-sidebar');
      default:
        return undefined;
    }
  }

  /** True when focus is in a text-editing surface (input / editor). */
  #isTextInputFocused(): boolean {
    const el =
      typeof document !== 'undefined'
        ? (document.activeElement as HTMLElement | null)
        : null;
    if (!el) return false;
    const tag = el.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return true;
    if (el.isContentEditable) return true;
    // Monaco / CodeMirror editor hosts.
    return !!el.closest?.('.monaco-editor, .cm-editor');
  }

  /** True when focus is inside the given slot AND that view is the visible one. */
  #isViewFocused(slot: string, viewId: string): boolean {
    if (typeof document === 'undefined') return false;
    const visible =
      punica.shell.Views?.manager?.getVisibleView?.(slot as any) ?? null;
    if (visible !== viewId) return false;
    const container = document.getElementById(slot);
    const el = document.activeElement as HTMLElement | null;
    return !!(container && el && container.contains(el));
  }

  /**
   * Evaluate whether the given shortcut is active in the current context.
   *
   * Supported `when` grammar (all clauses joined by `&&`):
   *   view == code            content-tab view type equality
   *   editorTextFocus         a known boolean context key
   *   !fileExplorerFocus      negation of a known key
   *
   * Any clause we cannot evaluate (unknown key, unknown left-hand side of
   * `==`) makes the whole expression FALSE — fail closed, never fail open.
   */
  #matchesContext(shortcut: shell.KeyboardShortcuts.IShortcut): boolean {
    const when = shortcut.when;
    if (!when) return true;

    return when
      .split('&&')
      .map((c) => c.trim())
      .filter((c) => c.length > 0)
      .every((clause) => this.#evalClause(clause));
  }

  #evalClause(clause: string): boolean {
    const eqIndex = clause.indexOf('==');
    if (eqIndex !== -1) {
      const left = clause.slice(0, eqIndex).trim();
      const right = clause
        .slice(eqIndex + 2)
        .trim()
        .replace(/^['"]|['"]$/g, '');
      if (left === 'view') {
        const view = punica.shell.ContentTabs?.getActiveViewType?.() ?? null;
        return view === right;
      }
      // Unknown left-hand side — cannot evaluate → fail closed.
      return false;
    }

    if (clause.startsWith('!')) {
      const value = this.#contextValue(clause.slice(1).trim());
      // Unknown key → cannot evaluate the negation → fail closed.
      return value === undefined ? false : !value;
    }

    return this.#contextValue(clause) === true;
  }

  /**
   *
   * @param shortcut
   */
  registerShortcut(shortcut: shell.KeyboardShortcuts.IShortcut): void {
    const duplicate = this.#shortcuts.find(
      (existing) =>
        this.#areKeysEqual(existing.keys, shortcut.keys) &&
        (existing.when ?? '') === (shortcut.when ?? '')
    );
    if (duplicate) {
      // eslint-disable-next-line no-console
      console.warn(
        `[KeyboardShortcuts] Ignoring duplicate binding for command "${shortcut.command}"; ` +
          `"${duplicate.command}" already owns this chord${shortcut.when ? ` (when: ${shortcut.when})` : ''}.`
      );
      return;
    }
    this.#shortcuts.push(shortcut);
  }

  /**
   *
   * @param keys
   */
  unregisterShortcut(keys: shell.KeyboardShortcuts.IKey[]): void {
    this.#shortcuts = this.#shortcuts.filter(
      (shortcut) => !this.#areKeysEqual(shortcut.keys, keys)
    );
  }

  /**
   *
   * @param event
   */
  handleKeyEvent(event: KeyboardEvent): void {
    // Ignore OS/browser auto-repeat: a held toggle key would otherwise flip
    // its target on every repeat (flicker).
    if (event.repeat) return;

    const matchedShortcut = this.#shortcuts.find(
      (shortcut) =>
        this.#areKeysPressed(event, shortcut.keys) &&
        this.#matchesContext(shortcut)
    );

    if (matchedShortcut) {
      event.preventDefault();

      punica.kernel.Commands.manager.executeCommand(matchedShortcut.command);
    }
  }
}

const keyboardShortcuts: typeof shell.KeyboardShortcuts = {
  manager: new KeyboardShortcutsManager()
};

export default keyboardShortcuts;
